import type { CoaSpecResultInput } from '@qcdoc/shared/types/coa/coa.types.ts';



import { createAsyncThunk } from '@reduxjs/toolkit';
import { extractErrorMessage } from '@qcdoc/shared/shared/utils/errorMessage.ts';
import { coaService as coaApi } from "@qcdoc/shared/services/repositories/coaService.ts";
import type { PagedRequest } from "@qcdoc/shared/types/common/common.ts";

import { vendorApi } from '@qcdoc/shared/services/repositories/vendorService.ts';
import type { AddVendorCoaRequest } from '@qcdoc/shared/types/vendor/vendor.types.ts';

function coaRequestError(error: unknown, fallback: string): string {
  if (typeof error === 'string') return error;
  const response = (error as { response?: { status?: number; data?: {
    error?: string; message?: string; detail?: string; errors?: Record<string, unknown>;
  } } })?.response;
  if (response?.status === 404) return 'The COA action is unavailable. The API may need updating, or access to this COA has changed.';
  if (response?.status === 401 || response?.status === 403) return 'Your session does not have permission for this COA action.';
  if (response?.status && response.status >= 500) return 'The COA service is temporarily unavailable. Your entries have been retained; retry later.';
  const body = response?.data;
  const validation = body?.errors && Object.values(body.errors).flat().filter(value => typeof value === 'string').join(' ');
  return validation || body?.error || body?.detail || body?.message || extractErrorMessage(error, fallback);
}

export const fetchList = createAsyncThunk('vendorCoa/list', async (args: [vendorId: string | undefined, params: PagedRequest], { signal, rejectWithValue }) => {
  try {
    signal.throwIfAborted();
    const [vendorId, params] = args;
    if (!vendorId) throw new Error("Vendor identity is required");
    const result = await (() => vendorApi.getCoaList(vendorId!, params).then(response => response.data))();
    signal.throwIfAborted();
    return structuredClone(result);
  } catch (error) {
    return rejectWithValue(extractErrorMessage(error, 'Unable to load list.'));
  }
});

export const mutateCreateCoa = createAsyncThunk('vendorCoa/createCoa', async (args: [dto: AddVendorCoaRequest], { signal, rejectWithValue }) => {
  try {
    signal.throwIfAborted();
    const [dto] = args;
    const result = await (() => vendorApi.createCoa(dto).then(response => response.data))();
    signal.throwIfAborted();
    return structuredClone(result);
  } catch (error) {
    return rejectWithValue(extractErrorMessage(error, 'Unable to save createCoa.'));
  }
});

export const fetchDocument = createAsyncThunk('vendorCoa/document', async (args: [id: string | undefined], { signal, rejectWithValue }) => {
  try {
    signal.throwIfAborted();
    const [id] = args;
    const result = await (async () => {
      const record = await coaApi.getById(id!, signal);
      if (!record.data) throw new Error('COA not found');
      const [pos, results] = await Promise.all([coaApi.getPos(id!, signal), coaApi.getResults(id!, signal)]);
      return { record: record.data, pos: pos.data, results: results.data };
    })();
    signal.throwIfAborted();
    return structuredClone(result);
  } catch (error) {
    return rejectWithValue(extractErrorMessage(error, 'Unable to load document.'));
  }
});

export const fetchWorkflow = createAsyncThunk('vendorCoa/workflow', async (args: [id: string | undefined], { signal, rejectWithValue }) => {
  try {
    signal.throwIfAborted();
    const [id] = args;
    const result = await (() => coaApi.getVendorWorkflow(id!, signal).then(response => response.data))();
    signal.throwIfAborted();
    return structuredClone(result);
  } catch (error) {
    return rejectWithValue(coaRequestError(error, 'Unable to load workflow.'));
  }
});

export const fetchSpecifications = createAsyncThunk('vendorCoa/specifications', async (args: [vqcaId: string | undefined], { signal, rejectWithValue }) => {
  try {
    signal.throwIfAborted();
    const [vqcaId] = args;
    const result = await (() => vendorApi.getVqcaSpecs(vqcaId!).then(response => response.data))();
    signal.throwIfAborted();
    return structuredClone(result);
  } catch (error) {
    return rejectWithValue(extractErrorMessage(error, 'Unable to load specifications.'));
  }
});

export const fetchVendorCoaAttachments = createAsyncThunk('vendorCoa/attachments', async (args: [id: string | undefined], { signal, rejectWithValue }) => {
  try {
    signal.throwIfAborted();
    const result = await (async () => { if (!args[0]) throw new Error("COA is required"); return coaApi.getAttachments(args[0], signal).then(response => response.data.map(({ docData: _docData, ...metadata }) => metadata)); })();
    signal.throwIfAborted();
    return structuredClone(result);
  } catch (error) { return rejectWithValue(extractErrorMessage(error, 'Unable to load attachments.')); }
});

export const fetchVendorCoaAgreements = createAsyncThunk('vendorCoa/agreements', async (args: [vendorId: string | undefined], { signal, rejectWithValue }) => {
  try {
    signal.throwIfAborted();
    const result = await (async () => { if (!args[0]) throw new Error("Vendor identity is required"); return vendorApi.getActiveVqcas(args[0]).then(response => response.data); })();
    signal.throwIfAborted();
    return structuredClone(result);
  } catch (error) { return rejectWithValue(extractErrorMessage(error, 'Unable to load agreements.')); }
});

export const checkVendorCoaDuplicate = createAsyncThunk('vendorCoa/duplicate', async (args: [request: { vqcaId: string; lotNumber: string }], { signal, rejectWithValue }) => {
  try {
    signal.throwIfAborted();
    const result = await (async () => { return vendorApi.checkCoaDuplicate(args[0]).then(response => response.data); })();
    signal.throwIfAborted();
    return structuredClone(result);
  } catch (error) { return rejectWithValue(extractErrorMessage(error, 'Unable to load duplicate.')); }
});

export const saveVendorCoa = createAsyncThunk('vendorCoa/save', async (args: [request: { id: string; vendorId: string; action: "po" | "note" | "product" | "performance"; text: string; results: CoaSpecResultInput[] }], { signal, rejectWithValue }) => {
  try {
    signal.throwIfAborted();
    const result = await (async () => { const [request] = args;
    if (!request.id) throw new Error('COA not found');
    if (request.action === 'po') {
      if (!request.text.trim()) throw new Error('Enter a PO number.');
      await coaApi.addPo(request.id, request.vendorId, request.text.trim(), '', signal);
    } else if (request.action === 'note') {
      if (!request.text.trim()) throw new Error('Enter a note.');
      await coaApi.appendVendorNote(request.id, request.text.trim(), signal);
    } else {
      if (!request.results.length || request.results.some(result => result.numVal == null && result.visualVal == null)) throw new Error('Enter an analysis for every specification.');
      await coaApi.addVendorResults(request.id, { notes: request.text.trim(), specResults: request.action === 'product' ? request.results : [], perfSpecResults: request.action === 'performance' ? request.results : [] }, signal);
    }
    return undefined; })();
    signal.throwIfAborted();
    return structuredClone(result);
  } catch (error) { return rejectWithValue(coaRequestError(error, 'The change could not be saved. Your entries have been retained.')); }
});
